import { useState, useMemo, useCallback } from "react";
import { z } from "zod";
import type { RendererMap, StepRenderer } from "./ChainOfThoughts";
import type {
  ToolsStepData,
  ContextStepData,
  RerankStepData,
  StrictStepData,
  PlanStepData,
  DetectLanguageStepData,
  ThinkingStepData,
  PrereqsStepData,
  ImagesStepData,
  ToolInvocationStepData,
  ToolResponseStepData,
  ChainOfThoughtStep,
  AdditionalContextStepData,
  TopicStepData,
} from "./ChainOfThoughts.types";
import { stepsToDisplay, ToolsNotAllowed } from "./ChainOfThoughts.types";
import styles from "./ChainOfThoughts.module.scss";
import { Badge, Flex, Text, Button, IconButton } from "@radix-ui/themes";
import { DownloadIcon } from "@radix-ui/react-icons";
import { MessageSources } from "../messages/messageSources";
import { useSimulatorOptional } from "../../context/SimulatorWrapper";
import { useChatOptional } from "../../context/ChatWrapper";
import { AdditionalContextView } from "../chat/StartChatWithContext";

export const ToolsRenderer: StepRenderer<"tools"> = ({ step }) => {
  const data: ToolsStepData = step;

  const filteredTools = data.allowedTools.filter(
    (tool) => !ToolsNotAllowed.includes(tool)
  );

  const unavailableTools = data.unavailable || [];

  return (
    <Flex direction="column" gap="3">
      <Flex align="center" gap="2">
        <Text size="1" weight="bold">
          Available Tools
        </Text>
        <Text size="1" color="gray">
          {filteredTools.length} tools
        </Text>
      </Flex>
      <Flex wrap="wrap" gap="2">
        {filteredTools.map((tool, i) => (
          <Badge key={i} size="1" color="blue">
            {tool}
          </Badge>
        ))}
      </Flex>
      {unavailableTools.length > 0 && (
        <Flex direction="column" gap="2">
          <Flex align="center" gap="2">
            <Text size="1" weight="bold">
              Unavailable Tools
            </Text>
            <Text size="1" color="gray">
              {unavailableTools.length} tools
            </Text>
          </Flex>
          <Flex direction="column" gap="2">
            {unavailableTools.map((unavailable, i) => (
              <Flex key={i} direction="column" gap="1">
                <Flex align="center" gap="2">
                  <Badge size="1" color="red">
                    {unavailable.toolName}
                  </Badge>
                  <Text size="1" color="gray">
                    Missing:
                  </Text>
                  <Text size="1" color="red">
                    {unavailable.missingKeys.join(", ")}
                  </Text>
                </Flex>
              </Flex>
            ))}
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

export const ContextRenderer: StepRenderer<"context"> = ({ step }) => {
  const data: ContextStepData = step;
  const [isQueryExpanded, setIsQueryExpanded] = useState(false);

  const shouldTruncate = data.userQuery && data.userQuery.length > 100;
  const displayQuery =
    shouldTruncate && !isQueryExpanded
      ? `${data.userQuery.substring(0, 100)}...`
      : data.userQuery;

  return (
    <Flex direction="column" gap="3">
      <Flex direction="column" gap="2">
        <Text size="1" weight="bold">
          User Query
        </Text>
        <Flex className={styles.queryTextContainer}>
          <Text size="1" style={{ fontStyle: "italic", lineHeight: 1.4 }}>
            "{displayQuery}"
          </Text>
        </Flex>
        {shouldTruncate && (
          <Button
            variant="ghost"
            size="1"
            onClick={() => setIsQueryExpanded(!isQueryExpanded)}
            style={{
              alignSelf: "flex-start",
              padding: "4px 8px",
              fontSize: "11px",
            }}
          >
            {isQueryExpanded ? "Show Less" : "Show More"}
          </Button>
        )}
      </Flex>

      <Flex direction="column" gap="2">
        <Text size="1" weight="bold">
          Allowed Sources
        </Text>
        <Flex wrap="wrap" gap="2">
          {data.allowedSources.map((source, i) => (
            <Badge key={i} size="1" color="green">
              {source}
            </Badge>
          ))}
        </Flex>
      </Flex>
    </Flex>
  );
};

export const RerankRenderer: StepRenderer<"rerank"> = ({ sources }) => {
  const [isSourcesOpen, setIsSourcesOpen] = useState(false);

  const context = useMemo(() => sources ?? [], [sources]);
  const { kbCount, ticketsCount, wikiCount } = useMemo(() => {
    const counters = context.reduce(
      (acc: Record<string, number>, item: any) => {
        if (item?.indexName) {
          const indexName = item.indexName;
          acc[indexName] = (acc[indexName] || 0) + 1;
        }
        return acc;
      },
      {}
    );

    return {
      kbCount: counters["quack-kb"] || 0,
      ticketsCount: counters["quack-tickets"] || 0,
      wikiCount: counters["quack-wiki"] || 0,
    };
  }, [context]);

  const hasContent = context.length > 0;

  return (
    <Flex direction="column" gap="3">
      <Flex align="center" justify="between">
        <Text size="1" weight="bold">
          Rank Context
        </Text>
        {hasContent && sources && sources.length > 0 && (
          <Button
            variant="outline"
            size="1"
            onClick={() => setIsSourcesOpen(true)}
          >
            View Sources ({sources.length})
          </Button>
        )}
      </Flex>

      <Flex gap="3" wrap="wrap">
        {kbCount > 0 && (
          <Flex
            direction="column"
            align="center"
            className={styles.contextCard}
          >
            <Text size="1" weight="bold" color="gray">
              KNOWLEDGE
            </Text>
            <Text size="3" weight="bold">
              {kbCount}
            </Text>
          </Flex>
        )}
        {ticketsCount > 0 && (
          <Flex
            direction="column"
            align="center"
            className={styles.contextCard}
          >
            <Text size="1" weight="bold" color="gray">
              TICKETS
            </Text>
            <Text size="3" weight="bold">
              {ticketsCount}
            </Text>
          </Flex>
        )}
        {wikiCount > 0 && (
          <Flex
            direction="column"
            align="center"
            className={styles.contextCard}
          >
            <Text size="1" weight="bold" color="gray">
              WIKI
            </Text>
            <Text size="3" weight="bold">
              {wikiCount}
            </Text>
          </Flex>
        )}
      </Flex>

      {hasContent && sources && sources.length > 0 && (
        <MessageSources
          isOpen={isSourcesOpen}
          setOpen={setIsSourcesOpen}
          sources={sources}
        />
      )}

      {!hasContent && (
        <Flex
          align="center"
          justify="center"
          className={styles.emptyContextContainer}
        >
          <Text size="2" color="gray">
            No context data available
          </Text>
        </Flex>
      )}
    </Flex>
  );
};

export const StrictRenderer: StepRenderer<"strict"> = ({ step }) => {
  const data: StrictStepData = step;
  return (
    <Flex direction="column" gap="3">
      <Flex align="center" gap="2">
        <Text size="1" weight="bold">
          Strict Mode
        </Text>
        <Badge size="1" color={data.isStrictMode ? "green" : "red"}>
          {data.isStrictMode ? "Enabled" : "Disabled"}
        </Badge>
      </Flex>
      <Flex align="center" gap="2">
        <Text size="1" weight="bold">
          Allowed
        </Text>
        <Badge size="1" color={data.isAllowed ? "green" : "red"}>
          {data.isAllowed ? "Yes" : "No"}
        </Badge>
      </Flex>
    </Flex>
  );
};

export const PlanRenderer: StepRenderer<"plan"> = ({ step }) => {
  const data: PlanStepData = step;
  return (
    <Flex direction="column" gap="2" style={{ width: "100%" }}>
      <Text size="1" weight="bold">
        Plan Content
      </Text>
      <Flex className={styles.planContentContainer} style={{ width: "100%" }}>
        <Text size="1">{data.planContent}</Text>
      </Flex>
    </Flex>
  );
};

export const DetectLanguageRenderer: StepRenderer<"detect-language"> = ({
  step,
}) => {
  const data: DetectLanguageStepData = step;
  return (
    <Flex align="center" gap="2">
      <Text size="1" weight="bold">
        Detected Language
      </Text>
      <Badge size="1" color="blue">
        {data.detectedLanguage}
      </Badge>
    </Flex>
  );
};

export const ThinkingRenderer: StepRenderer<"thinking"> = ({ step }) => {
  const data: ThinkingStepData = step;
  return (
    <Flex direction="column" gap="3">
      <Flex align="center" gap="2">
        <Text size="1" weight="bold">
          Thinking Needed
        </Text>
        <Badge size="1" color={data.isThinkingNeeded ? "green" : "red"}>
          {data.isThinkingNeeded ? "Yes" : "No"}
        </Badge>
      </Flex>
      <Flex direction="column" gap="1">
        <Text size="1" weight="bold">
          Reason
        </Text>
        <Text size="2">{data.reason}</Text>
      </Flex>
    </Flex>
  );
};

export const PrereqsRenderer: StepRenderer<"prereqs"> = ({ step }) => {
  const data: PrereqsStepData = step;
  const allPrerequisitesMet = data.prerequisites.isAllPrerequisitesMet;

  return (
    <Flex direction="column" gap="3">
      <Flex align="center" gap="2">
        <Text size="1" weight="bold">
          All Prerequisites Met
        </Text>
        <Badge size="1" color={allPrerequisitesMet ? "green" : "red"}>
          {allPrerequisitesMet ? "Yes" : "No"}
        </Badge>
      </Flex>

      {!allPrerequisitesMet && (
        <>
          <Flex direction="column" gap="1">
            <Text size="1" weight="bold">
              Provided
            </Text>
            <Text size="2">
              {data.prerequisites.prerequisitesProvided.join(", ")}
            </Text>
          </Flex>
          <Flex direction="column" gap="1">
            <Text size="1" weight="bold">
              Missing
            </Text>
            <Text size="2">
              {data.prerequisites.prerequisitesNotProvided.join(", ")}
            </Text>
          </Flex>
          <Flex direction="column" gap="1">
            <Text size="1" weight="bold">
              Reason
            </Text>
            <Text size="2">{data.prerequisites.reason}</Text>
          </Flex>
        </>
      )}
    </Flex>
  );
};

export const ImagesRenderer: StepRenderer<"images"> = ({ step }) => {
  const data: ImagesStepData = step;
  return (
    <Flex direction="column" gap="3">
      <Flex align="center" gap="2">
        <Text size="1" weight="bold">
          Processed Images
        </Text>
        <Badge size="1" color="blue">
          {data.processedImages.images.length} images
        </Badge>
      </Flex>

      {data.processedImages.images.length > 0 ? (
        <Flex gap="3" wrap="wrap">
          {data.processedImages.images.map((image, i) => (
            <Flex key={i} direction="column" className={styles.imageContainer}>
              <img
                src={image.image}
                alt="Processed"
                style={{
                  maxWidth: "100%",
                  maxHeight: "150px",
                  borderRadius: "6px",
                  objectFit: "cover",
                }}
              />
              <Text size="1" color="gray">
                {image.description}
              </Text>
            </Flex>
          ))}
        </Flex>
      ) : (
        <Flex
          align="center"
          justify="center"
          className={styles.emptyContextContainer}
        >
          <Text size="2" color="gray">
            No images were processed in this step
          </Text>
        </Flex>
      )}
    </Flex>
  );
};

export const ToolInvocationRenderer: StepRenderer<"tool-invocation"> = ({
  step,
}) => {
  const data: ToolInvocationStepData = step;
  return (
    <Flex direction="column" gap="3">
      <Flex align="center" gap="2">
        <Text size="1" weight="bold">
          Tool Name
        </Text>
        <Badge size="1" color="blue">
          {data.toolName}
        </Badge>
      </Flex>

      {Object.keys(data.toolArgs).length > 0 && (
        <Flex direction="column" gap="2">
          <Text size="1" weight="bold">
            Arguments
          </Text>
          <Flex direction="column" gap="1">
            {Object.entries(data.toolArgs).map(([key, value]) => (
              <Flex key={key} gap="2">
                <Text size="1" weight="bold" color="gray">
                  {key}:
                </Text>
                <Text size="1">
                  {typeof value === "object"
                    ? JSON.stringify(value, null, 2)
                    : String(value)}
                </Text>
              </Flex>
            ))}
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

const BrowserSkillResultSchema = z.object({
  data: z
    .object({
      recordingUrl: z.string().nullable().optional(),
    })
    .optional(),
});

export const ToolResponseRenderer: StepRenderer<"tool-response"> = ({
  step,
}) => {
  const data: ToolResponseStepData = step;

  const parsedResult = BrowserSkillResultSchema.safeParse(data.result);
  const recordingUrl = parsedResult.success
    ? parsedResult.data.data?.recordingUrl
    : undefined;

  return (
    <Flex direction="column" gap="3">
      <Flex align="center" gap="2">
        <Text size="1" weight="bold">
          Tool Name
        </Text>
        <Badge size="1" color="blue">
          {data.toolName}
        </Badge>
      </Flex>

      <Flex align="center" gap="2">
        <Text size="1" weight="bold">
          Status
        </Text>
        <Badge
          size="1"
          color={
            data.state === "result"
              ? "green"
              : data.state === "error"
              ? "red"
              : "yellow"
          }
        >
          {data.state}
        </Badge>
      </Flex>

      {recordingUrl && (
        <Flex align="center" gap="2">
          <Text size="1" weight="bold">
            Session Recording
          </Text>
          <a href={recordingUrl} download="recording.mp4">
            <IconButton variant="outline" size="1" asChild>
              <span>
                <DownloadIcon width={12} height={12} />
              </span>
            </IconButton>
          </a>
        </Flex>
      )}

      {data.result !== undefined && data.result !== null && (
        <Flex direction="column" gap="2">
          <Text size="1" weight="bold">
            Result
          </Text>
          <Flex className={styles.planContentContainer}>
            <Text size="1">
              {typeof data.result === "object" && data.result !== null
                ? JSON.stringify(data.result, null, 2)
                : String(data.result)}
            </Text>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

const AdditionalContextRenderer: StepRenderer<"additional-context"> = ({
  step,
}) => {
  const data: AdditionalContextStepData = step;
  const simulator = useSimulatorOptional();
  const chat = useChatOptional();
  const currentRelease = simulator?.currentRelease ?? null;
  const selectedTopic = chat?.selectedTopic ?? null;

  return (
    <Flex direction="column" gap="3">
      <Text size="1" weight="bold">
        Additional Context
      </Text>
      {currentRelease != null && selectedTopic != null ? (
        <AdditionalContextView
          topicId={selectedTopic.id}
          releaseId={currentRelease.id}
          value={data.additionalContext}
        />
      ) : (
        <Flex className={styles.planContentContainer}>
          <Text size="1">
            {typeof data.additionalContext === "object" &&
            data.additionalContext !== null
              ? JSON.stringify(data.additionalContext, null, 2)
              : String(data.additionalContext)}
          </Text>
        </Flex>
      )}
    </Flex>
  );
};

export const TopicRenderer: StepRenderer<"topic"> = ({ step }) => {
  const data: TopicStepData = step;

  return (
    <Flex align="center" gap="2">
      <Text size="1" weight="bold">
        Detected Topic
      </Text>
      <Badge size="1" color="purple">
        {data.topic}
      </Badge>
    </Flex>
  );
};

export const defaultRenderers: RendererMap = {
  tools: ToolsRenderer,
  context: ContextRenderer,
  rerank: RerankRenderer,
  strict: StrictRenderer,
  plan: PlanRenderer,
  "detect-language": DetectLanguageRenderer,
  thinking: ThinkingRenderer,
  prereqs: PrereqsRenderer,
  images: ImagesRenderer,
  "tool-invocation": ToolInvocationRenderer,
  "tool-response": ToolResponseRenderer,
  "additional-context": AdditionalContextRenderer,
  topic: TopicRenderer,
};

export function getVisibleSteps(
  steps: ChainOfThoughtStep[]
): ChainOfThoughtStep[] {
  return steps.filter((step) => {
    if (!stepsToDisplay.includes(step.stepKey)) {
      return false;
    }

    if (step.stepKey === "strict" && step.isStrictMode === false) {
      return false;
    }

    if (step.stepKey === "images") {
      const imagesData = step;
      if (imagesData.processedImages.images.length === 0) {
        return false;
      }
    }

    if (step.stepKey === "tools") {
      const toolsData = step;
      const filteredTools = toolsData.allowedTools.filter(
        (tool) => !ToolsNotAllowed.includes(tool)
      );
      const hasUnavailableTools =
        toolsData.unavailable && toolsData.unavailable.length > 0;
      if (filteredTools.length === 0 && !hasUnavailableTools) {
        return false;
      }
    }

    if (
      step.stepKey === "tool-invocation" ||
      step.stepKey === "tool-response"
    ) {
      const toolName = step.toolName?.toLowerCase() || "";
      if (ToolsNotAllowed.includes(toolName)) {
        return false;
      }
    }

    if (step.stepKey === "thinking") {
      const thinkingData = step;
      if (thinkingData.isThinkingNeeded === true) {
        return false;
      }
    }

    return true;
  });
}

export function getLoadingMessage(
  isLoading: boolean,
  hasTopic: boolean,
  steps: ChainOfThoughtStep[],
  customMessage?: string
): string | undefined {
  if (!isLoading) return undefined;

  if (customMessage) return customMessage;

  if (!hasTopic && steps.length === 0) {
    return "Classifying topic...";
  }

  if (hasTopic && steps.length === 0) {
    return "Loading Response...";
  }

  if (steps.length > 0) {
    const lastStep = steps[steps.length - 1];
    const stepKey = lastStep.stepKey;

    switch (stepKey) {
      case "prereqs":
        return "Checking Prerequisites...";
      case "context":
        return "Gathering Context...";
      case "rerank":
        return "Reranking Context...";
      case "strict":
        return "Checking Strict Mode...";
      case "plan":
        return "Planning Response...";
      case "detect-language":
        return "Detecting Language...";
      case "thinking":
        return "Analyzing...";
      case "images":
        return "Reviewing Images...";
      case "topic":
        return "Classifying topic...";
      case "tools":
        return "Checking if there relevant skills...";
      case "tool-invocation":
        return "Using Skill...";
      case "tool-response":
        return "Reviewing api response...";
      default:
        return "Processing...";
    }
  }

  return "Loading...";
}
